import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { User, Mail, Calendar, Tag, X } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { useMessage } from '../contexts/MessageContext';
import axios from 'axios';

/**
 * 프로필 팝오버 — 아바타 클릭 시 프로필보기 / 메세지 선택
 * Portal 기반으로 body에 렌더링하여 부모 overflow 영향 없음
 */
function ProfilePopover({ user, children }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const { openCompose } = useMessage() || {};

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let top = rect.bottom + 6;
    let left = rect.left;
    // 오른쪽 넘침 방지
    if (left + 270 > window.innerWidth) {
      left = window.innerWidth - 280;
    }
    // 아래쪽 넘침 방지 — 위로 표시
    if (top + 300 > window.innerHeight) {
      top = rect.top - 6;
    }
    setPos({ top, left, above: top === rect.top - 6 });
  }, []);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) {
        setOpen(false);
        setMode(null);
        setProfile(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 스크롤/리사이즈 시 위치 업데이트
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const handleProfile = async () => {
    setMode('profile');
    setLoading(true);
    try {
      const res = await axios.get('/api/user/profile', {
        params: { userType: user.userType, userId: user.userId },
        withCredentials: true,
      });
      setProfile(res.data);
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = () => {
    setOpen(false);
    setMode(null);
    openCompose?.(user);
  };

  const close = () => {
    setOpen(false);
    setMode(null);
    setProfile(null);
  };

  const popoverContent = open ? (
    <div
      ref={popoverRef}
      className="animate-fadeIn"
      style={{
        position: 'fixed',
        top: pos.above ? undefined : pos.top,
        bottom: pos.above ? `${window.innerHeight - pos.top}px` : undefined,
        left: pos.left,
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}>
      {!mode ? (
        <div className="bg-[#1a2b42] border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[140px]">
          <button
            onClick={handleProfile}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/[0.06] hover:text-white transition-colors">
            <User size={14} className="text-sky-400" />
            프로필
          </button>
          <div className="border-t border-white/[0.06]" />
          <button
            onClick={handleMessage}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/[0.06] hover:text-white transition-colors">
            <Mail size={14} className="text-emerald-400" />
            메세지
          </button>
        </div>
      ) : (
        <div className="bg-[#1a2b42] border border-white/10 rounded-2xl shadow-2xl overflow-hidden w-[300px]">
          <div className="relative h-24 bg-gradient-to-br from-sky-600/40 to-indigo-600/40">
            <button
              onClick={close}
              className="absolute top-2 right-2 p-1 rounded-full bg-black/20 text-white/60 hover:text-white hover:bg-black/40 transition-colors">
              <X size={12} />
            </button>
          </div>
          <div className="px-5 pb-5 -mt-16">
            <div className="flex justify-center">
              <div className="rounded-full border-4 border-[#1a2b42] bg-[#1a2b42]">
                <UserAvatar
                  name={user.name}
                  profileImage={user.profileImage}
                  userType={user.userType}
                  adminYn={user.adminYn}
                  size={120}
                  showRole={true}
                />
              </div>
            </div>
            <div className="text-center mt-3">
              <p className="text-sm font-bold text-white">{user.name}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {user.adminYn === 'Y' ? '관리자' : user.userType === 'client' ? '권리사' : '일반회원'}
              </p>
            </div>
            {loading ? (
              <div className="flex justify-center mt-4">
                <div className="w-5 h-5 border-2 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
              </div>
            ) : profile ? (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Tag size={12} className="text-violet-400 shrink-0" />
                  <span>분류</span>
                  <span className="ml-auto text-slate-300 font-medium">
                    {profile.category || '뮤지션'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Calendar size={12} className="text-sky-400 shrink-0" />
                  <span>가입일</span>
                  <span className="ml-auto text-slate-300">
                    {new Date(profile.createdAt).toLocaleDateString('ko-KR')}
                  </span>
                </div>
              </div>
            ) : null}
            <button
              onClick={handleMessage}
              className="w-full mt-4 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-sky-500/15 text-sky-400 text-xs font-semibold hover:bg-sky-500/25 transition-colors">
              <Mail size={13} />
              메세지 보내기
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          updatePosition();
          setOpen(!open);
          setMode(null);
          setProfile(null);
        }}
        className="inline-flex items-center gap-1.5 hover:text-sky-400 transition-colors cursor-pointer">
        {children}
      </button>
      {popoverContent && ReactDOM.createPortal(popoverContent, document.body)}
    </>
  );
}

export default ProfilePopover;
